import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import CodeSnippet from '../components/CodeSnippet';
import { PROJECT_INFO, FEATURES, API_ENDPOINTS, API_LIMITS } from '../utils/constants';

const Home = () => {
  const { isAuthenticated } = useAuth();

  const exampleCode = `curl -X GET "https://api.voting-data.gov.ua/v1/votings?law_id=123&limit=10" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json"`;

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="mb-6">{PROJECT_INFO.shortTitle}</h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">{PROJECT_INFO.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Link to="/profile"><Button size="lg">Перейти до профілю</Button></Link>
              ) : (
                <>
                  <Link to="/register"><Button size="lg">Почати використання</Button></Link>
                  <Link to="/login"><Button variant="secondary" size="lg">Увійти</Button></Link>
                </>
              )}
              <Link to="/docs"><Button variant="outline" size="lg">Документація</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="mb-4 text-center">Проблема, яку ми вирішуємо</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">{PROJECT_INFO.problem}</p>
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <p className="text-gray-800 leading-relaxed">{PROJECT_INFO.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Value */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4">Бізнес-цінність</h2>
            <p className="text-lg text-gray-600">{PROJECT_INFO.businessValue}</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="mb-4">Можливості системи</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">На основі технічного завдання системи збору та публікації структурованих даних</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <Card key={index} hover><div className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3><p className="text-gray-600">{feature.description}</p></div></Card>
            ))}
          </div>
        </div>
      </section>

      {/* NEW: Roles Section */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="mb-4">Ролі на платформі</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Різні користувачі мають різні рівні доступу та відповідальності</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Адміністратор системи</h3>
                <p className="text-gray-600 mb-4">Адміністратор системи має повний доступ до всіх компонентів Платформи. Відповідає за управління обліковими записами користувачів, налаштування ролей та дозволів у Keycloak, моніторинг стану сервісів та виконання резервного копіювання. Адміністратор також управляє конфігурацією Docker-контейнерів, мережевими налаштуваннями та скейлингом хмарної інфраструктури.</p>
                <p className="text-gray-600">Також відповідає за налаштування та управління процесами збору даних. За допомогою CnC-панелі він може налаштовувати конфігурації нових джерел, запускати та перезапускати Dagster-джобу, аналізувати помилки в логах та моніторити статус ETL-конвеєрів. Оператор може також переглядати вміст MinIO-сховища та управляти даними в Bronze-шарі.</p>
              </div>
            </Card>
            <Card>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Аудитор / Журналіст / Контент-менеджер / Партнер ОГС</h3>
                <p className="text-gray-600 mb-4">Аудитори та журналісти мають доступ до REST API з дозволом <code className="bg-gray-100 px-1 py-0.5 rounded">read:data</code>. Вони користуються Платформою для перевірки походження та достовірності даних про голосування, розслідування фактів та підготовки журналістських матеріалів. Високий рівень прозорості даних забезпечує можливість верифікації інформації незалежними особами.</p>
                <p className="text-gray-600">Контент-менеджери та партнери організацій громадянського суспільства надають дані про голосування місцевих органів влади, які не покриваються основними джерелами. Вони взаємодіють з Платформою через налагоджені формати обміну даними (JSON, CSV) або безпосередньо через REST API у режимі запису (якщо такий доступ надано).</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section id="api" className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="mb-4">REST API</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Авторизований доступ через JWT токени з багатими можливостями фільтрації</p>
          </div>
          <div className="mb-12">
            <h3 className="text-lg font-semibold mb-3">Приклад запиту</h3>
            <CodeSnippet code={exampleCode} />
          </div>
          <div className="mb-12">
            <h3 className="text-lg font-semibold mb-4">Основні ендпоїнти</h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200"><tr><th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Метод</th><th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Шлях</th><th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Опис</th></tr></thead>
                <tbody className="divide-y divide-gray-200">
                  {API_ENDPOINTS.map((endpoint, index) => (<tr key={index} className="hover:bg-gray-50"><td className="px-6 py-3"><span className="inline-flex px-2 py-1 text-xs font-mono font-semibold bg-blue-50 text-blue-700 rounded">{endpoint.method}</span></td><td className="px-6 py-3 font-mono text-sm text-gray-900">{endpoint.path}</td><td className="px-6 py-3 text-sm text-gray-600">{endpoint.description}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Ліміти та обмеження</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card><div className="p-4"><div className="text-2xl font-bold text-blue-600 mb-1">{API_LIMITS.rateLimit.split(' ')[0]}</div><p className="text-sm text-gray-600">{API_LIMITS.rateLimit}</p></div></Card>
              <Card><div className="p-4"><div className="text-2xl font-bold text-blue-600 mb-1">{API_LIMITS.tokenLifetime.split(' ')[0]}</div><p className="text-sm text-gray-600">{API_LIMITS.tokenLifetime}</p></div></Card>
              <Card><div className="p-4"><div className="text-2xl font-bold text-blue-600 mb-1">≤200</div><p className="text-sm text-gray-600">{API_LIMITS.paginationLimit}</p></div></Card>
            </div>
          </div>
        </div>
      </section>

      {/* Authentication Call to Action */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4">Почніть працювати з API</h2>
            <p className="text-gray-600 mb-6">Створіть особистий кабінет, отримайте API токен та почніть інтеграцію з нашою системою. Токен необхідний для автентифікації всіх запитів до API.</p>
            {!isAuthenticated && (<Link to="/register"><Button size="lg">Створити акаунт</Button></Link>)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;